import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import * as React from "react";
import styled, { css } from "styled-components";

interface IProps {
  children?: React.ReactNode | undefined;
  disabled?: boolean;
  isVisible: boolean;
  errorMessage?: React.ReactNode;
  onClickPromise: () => Promise<unknown>;
  "data-test-id"?: string;
}

export const StickySaveProfileButton: React.FC<IProps> = ({
  onClickPromise,
  disabled,
  isVisible,
  errorMessage,
  children,
  "data-test-id": dataTestId,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [loading, setLoading] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | undefined>(
    undefined
  );

  const handleButtonClicked = () => {
    setLoading(true);
    setSaveError(undefined);
    onClickPromise()
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setSaveError(
          strictLocalize({
            id: "W/5hwr",
            defaultMessage: "Something went wrong. Please try again.",
          })
        );
        setLoading(false);
      });
  };

  const errorToDisplay = errorMessage || saveError;

  return (
    <StickyColumn $isVisible={isVisible}>
      <StyledButton
        onClick={handleButtonClicked}
        disabled={disabled}
        data-test-id={dataTestId}
        loading={loading}
      >
        {children}
      </StyledButton>
      {errorToDisplay ? (
        <>
          <Spacer height="1x" />
          <Typography color={Core.text.critical}>{errorToDisplay}</Typography>
        </>
      ) : null}
    </StickyColumn>
  );
};

const StickyColumn = styled(Column)<{ $isVisible: boolean }>`
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: ${Core.surface.inverse};
  border-top: 1px solid ${Core.border.muted};
  width: calc(100% + 30px);
  margin: 20px -15px 0;
  padding: 24px 15px;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  ${(props) =>
    props.$isVisible
      ? css`
          opacity: 1;
          transform: translateY(0);
        `
      : css`
          opacity: 0;
          transform: translateY(100%);
        `};

  ${responsiveMediaQueries.desktopAndAbove} {
    width: calc(100% + 100px);
    margin: 20px -50px 0;
    padding: 24px 50px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 380px;
  }
`;
